import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import "@/styles/vault.css";

/**
 * Public landing page hit when a hunter scans a Vault QR poster:
 *   /vault/qr/:token
 *
 * If logged in: posts to /vault/qr/{token}/claim and shows a celebratory
 * reveal of the granted character. If not logged in: punts to /login with
 * a return URL so they don't lose the claim.
 */
export default function VaultQrLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    // Wait for auth to resolve before deciding what to do.
    if (loading) return;
    if (!user) {
      // Hold the token in a return URL so we can claim post-login.
      const returnTo = encodeURIComponent(`/vault/qr/${token}`);
      navigate(`/login?next=${returnTo}`, { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post(`/vault/qr/${token}/claim`);
        setState({ status: "ok", data });
      } catch (e) {
        setState({ status: "error", message: formatApiError(e.response?.data?.detail) || "Couldn't claim that fragment." });
      }
    })();
  }, [token, user, loading, navigate]);

  if (state.status === "loading") {
    return (
      <div className="py-20 text-center" data-testid="vault-qr-loading">
        <Loader2 size={32} className="inline animate-spin text-[#D7A62A]" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="py-20 text-center vault-stage max-w-lg mx-auto" data-testid="vault-qr-error">
        <AlertTriangle size={32} className="inline text-[#E53935] mb-2" />
        <div className="font-display uppercase text-2xl">Couldn't unlock</div>
        <div className="font-mono text-xs text-[#F5F2E8]/65 mt-2">{state.message}</div>
        <Link to="/vault" className="btn-loot text-xs mt-4 inline-block">See vaults</Link>
      </div>
    );
  }

  const d = state.data || {};
  return (
    <div className="py-12 vault-stage max-w-lg mx-auto text-center" data-testid="vault-qr-success">
      <Sparkles size={32} className="inline text-[#D7A62A] mb-2" />
      <div className="overline" style={{ color: "#D7A62A" }}>fragment unlocked</div>
      <div className="frag-chip is-revealed mx-auto mt-3" style={{ width: 96, height: 120, fontSize: "3rem" }}>
        {d.char}
      </div>
      <div className="font-display uppercase text-xl mt-4">
        Slot {d.position + 1} added to your vault.
      </div>
      {d.already_granted && (
        <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">(you already had this one)</div>
      )}
      <button
        onClick={() => navigate(`/vault/${d.vault_id}`)}
        className="btn-loot text-xs mt-4 inline-block"
        data-testid="vault-qr-go"
      >
        Open the vault →
      </button>
    </div>
  );
}
